import React, { useState, useEffect, useMemo } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import TaskDailyModal from "./TaskDailyModal";
import "./TaskDailyList.css";

const PAGE_SIZE = 10;

const TaskDailyList = ({ filterStatus, filterDate, onReload }) => {
  const [dailies, setDailies] = useState([]);
  const [taskJDs, setTaskJDs] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  // State cho tính năng Sắp xếp (Sort)
  const [sortField, setSortField] = useState("workDate"); // Mặc định sort theo Ngày
  const [sortAsc, setSortAsc] = useState(false); // Mặc định giảm dần (mới nhất lên đầu)

  // 1. Mặc định: Đầu tháng hiện tại -> Cuối tháng hiện tại (YYYY-MM-DD)
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  });

  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  });

  const userId = String(localStorage.getItem("userId") || "");

  const getStartOfDay = (dateInput) => {
    const d = new Date(dateInput);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  // Helper lấy tên Project linh hoạt
  const getProjectName = (item) => {
    return item.projectName || item.project?.name || item.projectNameDisplay || item.projectId || "—";
  };

  // 2. Fetch Data từ API
  const fetchData = async () => {
    if (!fromDate || !toDate) return;

    try {
      const endpoint = API_ENDPOINTS.taskdailiesEveryDay || `${API_ENDPOINTS.taskdailies}/everyday`;

      const res = await axiosClient.get(endpoint, {
        params: {
          fromDate: fromDate,
          toDate: toDate
        }
      });

      const jdRes = await axiosClient.get(API_ENDPOINTS.taskjds);
      const kpiRes = await axiosClient.get(API_ENDPOINTS.kpis);

      const normalizedDailies = (res.data || []).map((item) => ({
        ...item,
        kpiId: item.kpiId || item.KPIId || ""
      }));

      setDailies(normalizedDailies);
      setTaskJDs(jdRes.data || []);
      setKpis(kpiRes.data || []);
    } catch (error) {
      console.error("Fetch Task Dailies Error:", error.response?.data || error);
    }
  };

  // Auto reload khi component vừa render hoặc khi từ ngày/đến ngày thay đổi
  useEffect(() => {
    fetchData();
  }, [filterDate, fromDate, toDate]);

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  // Handler khi click vào Title tiêu đề cột để Sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc); // Đảo chiều nếu bấm lại cột cũ
    } else {
      setSortField(field);
      setSortAsc(true); // Mặc định tăng dần khi chọn cột mới
    }
  };

  // Render Icon hướng mũi tên cho Sort
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="fas fa-sort text-muted ms-1 opacity-50" style={{ fontSize: "0.75rem" }}></i>;
    }
    return sortAsc ? (
      <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: "0.85rem" }}></i>
    ) : (
      <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: "0.85rem" }}></i>
    );
  };

  // Filter client-side & Dynamic Sorting
  const filteredDailies = useMemo(() => {
    const todayTs = getStartOfDay(new Date());

    // 1. Filter
    const filtered = dailies.filter((d) => {
      if (userId && String(d.userId) !== userId) return false;

      const pName = getProjectName(d);
      const matchesSearch =
        (d.taskCode || "").toLowerCase().includes(search.toLowerCase()) ||
        (d.taskDetail || "").toLowerCase().includes(search.toLowerCase()) ||
        pName.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;

      const itemTs = getStartOfDay(d.workDate);

      if (filterStatus === "Today") return itemTs === todayTs;
      if (filterStatus === "Scheduling") return itemTs > todayTs;
      if (filterDate) return itemTs === getStartOfDay(filterDate);

      return true;
    });

    // 2. Sort
    return filtered.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === "projectName") {
        valA = getProjectName(a);
        valB = getProjectName(b);
      } else if (sortField === "workDate") {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      } else if (typeof valA === "string") {
        valA = (valA || "").toLowerCase();
        valB = (valB || "").toLowerCase();
      } else {
        valA = valA || 0;
        valB = valB || 0;
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [dailies, filterDate, filterStatus, search, userId, sortField, sortAsc]);

  // Phân trang
  const totalPages = Math.ceil(filteredDailies.length / PAGE_SIZE) || 1;
  const currentData = filteredDailies.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // 3. Thêm / Sửa task
  const handleSave = async (payload) => {
    try {
      if (editingItem) {
        await axiosClient.put(`${API_ENDPOINTS.taskdailies}/${editingItem.id}`, payload);
      } else {
        await axiosClient.post(API_ENDPOINTS.taskdailies, payload);
      }
      setShowModal(false);
      setEditingItem(null);
      await fetchData();
      if (onReload) onReload();
    } catch (error) {
      alert("Save failed!");
    }
  };

  // 4. Xóa task
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure?")) {
      try {
        await axiosClient.delete(`${API_ENDPOINTS.taskdailies}/${id}`);
        await fetchData();
        if (onReload) onReload();
      } catch (error) {
        alert("Delete failed!");
      }
    }
  };

  const thStyle = {
    backgroundColor: "#f8f9fc",
    color: "#4e73df",
    fontWeight: "bold",
    borderBottom: "2px solid #e3e6f0",
    cursor: "pointer",
    userSelect: "none"
  };

  return (
    <div className="p-0">
      {/* Top Filter Bar */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <label className="small fw-bold text-muted mb-0">From:</label>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: "140px" }}
            value={fromDate}
            onChange={handleFromDateChange}
          />
          <label className="small fw-bold text-muted mb-0">To:</label>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: "140px" }}
            value={toDate}
            onChange={handleToDateChange}
          />
        </div>

        <button
          className="btn btn-sm btn-success fw-bold rounded-pill shadow-sm"
          onClick={() => {
            setEditingItem(null);
            setShowModal(true);
          }}
        >
          <i className="fas fa-plus me-1"></i> Add New Task
        </button>
      </div>

      {/* Main Table Card */}
      <div className="card border-0 shadow-none bg-transparent">
        <div className="card-body p-0">
          <div className="table-responsive" style={{ minHeight: "80px" }}>
            <table className="table table-hover align-middle mb-0" style={{ fontSize: "0.85rem" }}>
              <thead>
                <tr className="text-center">
                  <th
                    className="ps-4 text-start"
                    style={thStyle}
                    onClick={() => handleSort("workDate")}
                  >
                    Date {renderSortIcon("workDate")}
                  </th>
                  <th
                    style={thStyle}
                    onClick={() => handleSort("taskCode")}
                  >
                    Task {renderSortIcon("taskCode")}
                  </th>
                  <th
                    className="text-start"
                    style={thStyle}
                    onClick={() => handleSort("projectName")}
                  >
                    Project {renderSortIcon("projectName")}
                  </th>
                  <th
                    className="text-start"
                    style={thStyle}
                    onClick={() => handleSort("taskDetail")}
                  >
                    Detail {renderSortIcon("taskDetail")}
                  </th>
                  <th
                    style={thStyle}
                    onClick={() => handleSort("status")}
                  >
                    Status {renderSortIcon("status")}
                  </th>
                  <th
                    style={thStyle}
                    onClick={() => handleSort("hours")}
                  >
                    Hours {renderSortIcon("hours")}
                  </th>
                  <th className="pe-4" style={{ ...thStyle, cursor: "default" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((d) => (
                    <tr key={d.id} className="text-center">
                      <td className="ps-4 text-start text-muted">
                        {new Date(d.workDate).toLocaleDateString("en-GB")}
                      </td>
                      <td>
                        <span className="badge bg-light text-primary border">{d.taskCode}</span>
                      </td>
                      <td className="text-start">
                        <span className="badge bg-light text-secondary border fw-normal" title={getProjectName(d)}>
                          {getProjectName(d)}
                        </span>
                      </td>
                      <td className="text-start">
                        <div
                          className="text-truncate"
                          style={{ maxWidth: "260px" }}
                          title={d.taskDetail}
                        >
                          {d.taskDetail}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge rounded-pill ${
                            d.status === "Done"
                              ? "bg-success"
                              : d.status === "Scheduling"
                              ? "bg-info"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {d.status}
                        </span>
                      </td>
                      <td className="fw-bold">{d.hours}h</td>
                      <td className="pe-4">
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm text-primary p-0"
                            onClick={() => handleEditClick(d)}
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="btn btn-sm text-danger p-0"
                            onClick={() => handleDelete(d.id)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-muted small italic">
                      No tasks found for the selected period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center p-2 bg-light border-top">
              <span className="small text-muted">
                Page {currentPage}/{totalPages}
              </span>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Task Daily Modal */}
      <TaskDailyModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        initialData={editingItem}
        taskJDs={taskJDs}
        kpis={kpis}
      />
    </div>
  );
};

export default TaskDailyList;